// Finish handoff (spec C6; the Record board's Finish handoff dialog, Dialog and ConfirmPopup cards):
// the AE with an accepted handoff returns the renewal to whoever it came from, at the step the VA
// picks up from (S15 batch 6b), or closes it themselves: Renewed, Rewritten with another carrier,
// Lost or Not renewed (batch 6c). A rewrite the VA still sends to the bank goes back to them at Proof
// to bank once the new carrier's dec page is attached. Both need the outcome note. Copy for AMS360
// holds the activity description and the sheet comment to paste by hand; nothing is written to
// AMS360. Opened by use-handoff-answers.tsx.
import { useState, type ReactNode } from 'react';
import type { ContinueAtStep, HandoffClose, StepsView } from '../auth/api';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { Dialog } from '../shell/Dialog';
import { CheckIcon, ChevronDownIcon, FileIcon, UploadIcon } from '../shell/icons';
import {
  bankConfirm,
  bankStillToDo,
  closeAmsCopy,
  closeButton,
  closeNoteHelp,
  closeOf,
  emptyCloseForm,
  endOptions,
  lostReasonOptions,
  returnAmsCopy,
  returnChoice,
  returnConfirm,
  returnNoteHelp,
  toBank,
  type AnswerRow,
  type CloseForm,
} from './ae-handoff-lines';
import { noteMax } from './notes-lines';
import { continueAtOptions } from './step-lines';
import { pickProblem } from './upload-lines';

/** New carrier's last choice: a carrier the list does not have, typed. */
const otherCarrier = 'Other';

/** The kit's dropdown (never the browser's): a button and a listbox of options. */
function Select({
  id,
  label,
  value,
  open,
  onToggle,
  options,
  onPick,
}: {
  id: string;
  label: ReactNode;
  value: string;
  open: boolean;
  onToggle: (open: boolean) => void;
  options: { key: string; label: string; hint?: string | null; disabled?: boolean }[];
  onPick: (key: string) => void;
}) {
  const picked = options.find((o) => o.key === value);
  return (
    <div>
      <span className="flab" id={`${id}-l`}>
        {label}
      </span>
      <div className="sel">
        <button
          type="button"
          className="sel-b"
          id={id}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={`${id}-l ${id}`}
          onClick={() => onToggle(!open)}
        >
          <span className="sel-v">{picked?.label ?? 'Choose'}</span>
          <ChevronDownIcon className="ico s12" />
        </button>
        {open && (
          <>
            <button
              type="button"
              className="pop-scrim"
              aria-label="Close list"
              onClick={() => onToggle(false)}
            />
            <div className="menu sel-m" role="listbox" aria-labelledby={`${id}-l`}>
              {options.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  className="mi"
                  role="option"
                  aria-selected={o.key === value}
                  disabled={o.disabled}
                  onClick={() => {
                    onPick(o.key);
                    onToggle(false);
                  }}
                >
                  <span className="dd-ck">
                    {o.key === value && <CheckIcon className="ico s12" />}
                  </span>
                  <span className="col g2" style={{ minWidth: 0 }}>
                    <span>{o.label}</span>
                    {o.hint && <span className="sel-h">{o.hint}</span>}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function FinishHandoffDialog({
  row,
  steps,
  initials,
  carriers,
  busy,
  onClose,
  onReturn,
  onCloseIt,
  initial,
}: {
  row: AnswerRow;
  steps: StepsView;
  /** The AE's initials, at the end of the sheet comment. */
  initials: string;
  /** The New carrier list for a rewrite; Other takes any name. */
  carriers: string[];
  busy: boolean;
  onClose: () => void;
  /** True once returned; the reason it was not, otherwise. */
  onReturn: (note: string, step: ContinueAtStep, title: string) => Promise<string | true>;
  /**
   * Close it myself: true once closed (or sent back for the bank step), else why not. `decPage` is
   * the new carrier's dec page, uploaded first, for a rewrite returned for the bank step.
   */
  onCloseIt: (
    note: string,
    sheetComment: string,
    how: HandoffClose,
    decPage: File | null,
  ) => Promise<string | true>;
  /** Where the dialog opens: Return by default (the layout checks open Close it myself too). */
  initial?: { mode: 'close'; form?: Partial<CloseForm>; carrier?: string; decPage?: File };
}) {
  const [{ options, stopped }] = useState(() => continueAtOptions(steps));
  const [mode, setMode] = useState<'return' | 'close'>(initial?.mode ?? 'return');
  const [step, setStep] = useState<ContinueAtStep>(stopped);
  const [listOpen, setListOpen] = useState<'step' | 'carrier' | null>(null);
  const [form, setForm] = useState<CloseForm>({ ...emptyCloseForm, ...initial?.form });
  // New carrier as picked from the list; Other shows a box for the name, kept in form.carrier.
  const [carrierPick, setCarrierPick] = useState(initial?.carrier ?? '');
  const [decPage, setDecPage] = useState<File | null>(initial?.decPage ?? null);
  const [note, setNote] = useState('');
  // Copy for AMS360 follows the choices until the AE edits a line.
  const [activity, setActivity] = useState<string | null>(null);
  const [sheet, setSheet] = useState<string | null>(null);
  const [copied, setCopied] = useState<'activity' | 'sheet' | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bankOffered = bankStillToDo(steps);
  const closing = mode === 'close';
  const bank = closing && bankOffered && toBank(form);
  const picked = options.find((o) => o.key === step) ?? options[0];
  const prefilled = closing
    ? closeAmsCopy(steps.activity.description, form, row.renewalPremium ?? null, initials)
    : returnAmsCopy(steps.activity.description, picked.title, initials);
  const ams = { activity: activity ?? prefilled.activity, sheet: sheet ?? prefilled.sheet };
  const text = note.trim();
  const close = closeOf({ ...form, bank });
  const ready = closing
    ? !!text && 'how' in close && !!ams.sheet.trim() && (!bank || !!decPage)
    : !!text;
  const subline = [
    row.insured,
    row.carrier ?? 'Carrier not in AMS360',
    row.line,
    row.policyNumber,
  ].join(' | ');

  /** A change to the choices: the copy lines follow it again. */
  const change = (p: Partial<CloseForm>) => {
    setForm((f) => ({ ...f, ...p }));
    setActivity(null);
    setSheet(null);
    setCopied(null);
    setError(null);
  };
  const copy = async (k: 'activity' | 'sheet') => {
    try {
      await navigator.clipboard.writeText(ams[k]);
      setCopied(k);
    } catch {
      setError('Could not copy. Select the text and copy it by hand.');
    }
  };
  const send = async () => {
    const result = await onReturn(text, picked.key, picked.title);
    if (result !== true) {
      setConfirming(false);
      setError(result);
    }
  };
  const closeIt = async () => {
    if (!('how' in close)) return;
    const result = await onCloseIt(text, ams.sheet.trim(), close.how, bank ? decPage : null);
    if (result !== true) {
      setConfirming(false);
      setError(result);
    }
  };
  const pickDec = (file: File | undefined) => {
    if (!file) return;
    const problem = pickProblem(file);
    setError(problem);
    setDecPage(problem ? null : file);
  };

  const button = closing ? closeButton({ ...form, bank }) : returnChoice(row.handoff);
  const choice = (m: 'return' | 'close', title: string, line: string) => (
    <button
      type="button"
      role="radio"
      className={mode === m ? 'opt on' : 'opt'}
      aria-checked={mode === m}
      onClick={() => {
        setMode(m);
        setActivity(null);
        setSheet(null);
        setCopied(null);
        setError(null);
      }}
    >
      <span className="rdot" />
      <span className="col g2">
        <span className="t13 w6 ink">{title}</span>
        <span className="t12 text2 lh">{line}</span>
      </span>
    </button>
  );

  return (
    <>
      <Dialog
        id="fin-t"
        title="Finish handoff"
        subline={subline}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className={ready ? 'btn btn-primary ml-auto' : 'btn ml-auto'}
              disabled={!ready || busy}
              onClick={() => (closing && !bank ? void closeIt() : setConfirming(true))}
            >
              {busy && closing && !bank ? 'Closing' : button}
            </button>
          </>
        }
      >
        <div>
          <div className="flab">What happens to the renewal</div>
          <div className="opts" role="radiogroup" aria-label="What happens to the renewal">
            {choice(
              'return',
              returnChoice(row.handoff),
              'Back in their queue. Status goes back to Pending.',
            )}
            {choice(
              'close',
              'Close it myself',
              'You finished the renewal. It leaves the VA queue.',
            )}
          </div>
        </div>
        {!closing && (
          <div>
            <Select
              id="fin-step"
              label="Continue at"
              value={step}
              open={listOpen === 'step'}
              onToggle={(o) => setListOpen(o ? 'step' : null)}
              options={options}
              onPick={(k) => {
                setStep(k as ContinueAtStep);
                setActivity(null);
                setSheet(null);
              }}
            />
            <p className="fnote">
              Pick Proof to bank if you rewrote it and only the bank step is left.
            </p>
          </div>
        )}
        {closing && (
          <>
            <div>
              <div className="flab" id="fin-end-l">
                How did it end? <span className="req">*</span>
              </div>
              <div className="fopts" role="group" aria-labelledby="fin-end-l">
                {endOptions.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className={form.end === o.key ? 'chip chip-sm on' : 'chip chip-sm'}
                    aria-pressed={form.end === o.key}
                    onClick={() => change({ end: o.key })}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            {form.end === 'rewritten' && (
              <div className="subform">
                <div className="sf3">
                  <Select
                    id="nw-car"
                    label={
                      <>
                        New carrier <span className="req">*</span>
                      </>
                    }
                    value={carrierPick}
                    open={listOpen === 'carrier'}
                    onToggle={(o) => setListOpen(o ? 'carrier' : null)}
                    options={[...carriers, otherCarrier].map((c) => ({ key: c, label: c }))}
                    onPick={(c) => {
                      setCarrierPick(c);
                      change({ carrier: c === otherCarrier ? '' : c });
                    }}
                  />
                  <div>
                    <label htmlFor="nw-prem" className="flab">
                      New premium <span className="req">*</span>
                    </label>
                    <input
                      id="nw-prem"
                      className="input"
                      type="text"
                      inputMode="decimal"
                      placeholder="$0"
                      value={form.premium}
                      onChange={(e) => change({ premium: e.target.value })}
                    />
                  </div>
                  <div>
                    <label htmlFor="nw-pol" className="flab">
                      New policy # <span className="req">*</span>
                    </label>
                    <input
                      id="nw-pol"
                      className="input mono"
                      type="text"
                      maxLength={40}
                      value={form.policyNumber}
                      onChange={(e) => change({ policyNumber: e.target.value })}
                    />
                  </div>
                </div>
                {carrierPick === otherCarrier && (
                  <div>
                    <label htmlFor="nw-car-name" className="flab">
                      Carrier name <span className="req">*</span>
                    </label>
                    <input
                      id="nw-car-name"
                      className="input"
                      type="text"
                      maxLength={100}
                      value={form.carrier}
                      onChange={(e) => change({ carrier: e.target.value })}
                    />
                  </div>
                )}
                {bankOffered && (
                  <label className="ckline">
                    <input
                      type="checkbox"
                      checked={form.bank}
                      onChange={(e) => change({ bank: e.target.checked })}
                    />
                    VA still needs to do the bank step
                  </label>
                )}
                {bank && (
                  <div>
                    <div className="flab">
                      New policy dec page <span className="req">*</span>
                    </div>
                    {decPage ? (
                      <div className="filepick on">
                        <FileIcon />
                        <span className="ink w5">{decPage.name}</span>
                        <button
                          type="button"
                          className="btn btn-sm ml-auto"
                          onClick={() => setDecPage(null)}
                        >
                          Replace
                        </button>
                      </div>
                    ) : (
                      <label className="filepick">
                        <UploadIcon />
                        <span>Attach the new carrier's dec page</span>
                        <span className="btn btn-sm ml-auto">Upload PDF</span>
                        <input
                          className="sr"
                          type="file"
                          accept="application/pdf"
                          onChange={(e) => {
                            pickDec(e.target.files?.[0]);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                    <p className="fnote">The VA sends this to the bank.</p>
                  </div>
                )}
              </div>
            )}
            {form.end === 'lost' && (
              <div className="subform">
                <div>
                  <div className="flab" id="lost-why-l">
                    Reason <span className="req">*</span>
                  </div>
                  <div className="fopts" role="group" aria-labelledby="lost-why-l">
                    {lostReasonOptions.map((o) => (
                      <button
                        key={o.key}
                        type="button"
                        className={form.lostReason === o.key ? 'chip chip-sm on' : 'chip chip-sm'}
                        aria-pressed={form.lostReason === o.key}
                        onClick={() => change({ lostReason: o.key })}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="lost-to" className="flab">
                    Went to <span className="optl">(optional)</span>
                  </label>
                  <input
                    id="lost-to"
                    className="input"
                    type="text"
                    maxLength={100}
                    placeholder="Agency or carrier"
                    value={form.wentTo}
                    onChange={(e) => change({ wentTo: e.target.value })}
                  />
                </div>
              </div>
            )}
            {form.end === 'not_renewed' && (
              <div className="subform">
                <div>
                  <label htmlFor="nr-reason" className="flab">
                    Reason <span className="req">*</span>
                  </label>
                  <input
                    id="nr-reason"
                    className="input"
                    type="text"
                    maxLength={300}
                    placeholder="For example: roof age, carrier non-renewed"
                    value={form.reason}
                    onChange={(e) => change({ reason: e.target.value })}
                  />
                </div>
              </div>
            )}
          </>
        )}
        <div>
          <label htmlFor="fin-note" className="flab">
            Outcome note <span className="req">*</span>
          </label>
          <textarea
            id="fin-note"
            className="input"
            placeholder="What did you do and what was decided?"
            maxLength={noteMax}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="fnote">
            {closing && !bank ? closeNoteHelp(form.end) : returnNoteHelp(row.handoff)}
          </p>
        </div>
        <div className="ams">
          <div className="row between">
            <span className="flab m0">Copy for AMS360</span>
            <span className="t12 muted">Prefilled, edit if needed</span>
          </div>
          <div className="ams-r">
            <label htmlFor="ams-act" className="t12 text2">
              Activity description
            </label>
            <input
              id="ams-act"
              className="input mono"
              type="text"
              value={ams.activity}
              onChange={(e) => {
                setActivity(e.target.value);
                setCopied(null);
              }}
            />
            <button type="button" className="btn btn-sm" onClick={() => void copy('activity')}>
              {copied === 'activity' ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="ams-r">
            <label htmlFor="ams-sheet" className="t12 text2">
              Sheet comment
            </label>
            <input
              id="ams-sheet"
              className="input mono"
              type="text"
              maxLength={300}
              value={ams.sheet}
              onChange={(e) => {
                setSheet(e.target.value);
                setCopied(null);
              }}
            />
            <button type="button" className="btn btn-sm" onClick={() => void copy('sheet')}>
              {copied === 'sheet' ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
        {error && (
          <p className="ferr" role="alert">
            {error}
          </p>
        )}
      </Dialog>
      {confirming && !closing && (
        <ConfirmPopup
          {...returnConfirm(row.insured, row.handoff, picked.title)}
          action={busy ? 'Returning' : 'Return'}
          busy={busy}
          onConfirm={() => void send()}
          onCancel={() => setConfirming(false)}
        />
      )}
      {confirming && bank && (
        <ConfirmPopup
          {...bankConfirm(row.insured, row.handoff)}
          action={busy ? 'Returning' : 'Return'}
          busy={busy}
          onConfirm={() => void closeIt()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}
